import React, { useState, useEffect, useCallback } from 'react';
import AdminDashboardLayout from 'pages/admin-dashboard-overview/components/AdminDashboardLayout';
import { supabase } from '../../lib/supabase';

const reasonLabel = (reason) => {
  if (reason === 'founder') return 'Fondatore';
  if (reason === 'manual') return 'Manuale';
  return reason || '—';
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr)?.toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const AdminProgrammaVip = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [removingId, setRemovingId] = useState(null);

  // Stats
  const [stats, setStats] = useState(null);

  // Soglia card
  const [soglia, setSoglia] = useState('');
  const [sogliaLoading, setSogliaLoading] = useState(false);
  const [sogliaError, setSogliaError] = useState(null);
  const [sogliaSuccess, setSogliaSuccess] = useState(false);

  // Badge card
  const [badgeLabel, setBadgeLabel] = useState('');
  const [badgeIcon, setBadgeIcon] = useState('');
  const [badgeLoading, setBadgeLoading] = useState(false);
  const [badgeError, setBadgeError] = useState(null);
  const [badgeSuccess, setBadgeSuccess] = useState(false);

  // Aggiungi manuale card
  const [addEmail, setAddEmail] = useState('');
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState(null);
  const [addSuccess, setAddSuccess] = useState(false);

  const fetchStats = useCallback(async () => {
    const { data } = await supabase?.rpc('admin_vip_stats');
    if (data?.[0]) {
      setStats(data?.[0]);
      setSoglia(String(data?.[0]?.threshold ?? ''));
    }
  }, []);

  const fetchBadge = useCallback(async () => {
    const { data } = await supabase?.rpc('get_vip_badge');
    if (data?.[0]) {
      setBadgeLabel(data?.[0]?.label ?? '');
      setBadgeIcon(data?.[0]?.icon ?? '');
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: rpcError } = await supabase?.rpc('admin_list_exempt_users');
    if (rpcError) {
      setError(rpcError?.message);
    } else {
      setUsers(data || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchStats();
    fetchBadge();
    fetchUsers();
  }, [fetchStats, fetchBadge, fetchUsers]);

  const handleRemove = async (userId) => {
    setRemovingId(userId);
    const { error: rpcError } = await supabase?.rpc('admin_set_user_exemption', {
      p_user_id: userId,
      p_exempt: false,
    });
    if (rpcError) {
      alert('Errore nella rimozione: ' + rpcError?.message);
    } else {
      await fetchUsers();
      await fetchStats();
    }
    setRemovingId(null);
  };

  const handleSogliaUpdate = async () => {
    setSogliaLoading(true);
    setSogliaError(null);
    setSogliaSuccess(false);
    try {
      const { error: rpcError } = await supabase?.rpc('admin_set_vip_threshold', {
        p_new: parseInt(soglia, 10),
      });
      if (rpcError) throw rpcError;
      setSogliaSuccess(true);
      await fetchStats();
      setTimeout(() => setSogliaSuccess(false), 2500);
    } catch (err) {
      setSogliaError(err?.message || 'Errore aggiornamento soglia');
    }
    setSogliaLoading(false);
  };

  const handleBadgeSave = async () => {
    setBadgeLoading(true);
    setBadgeError(null);
    setBadgeSuccess(false);
    try {
      const { error: rpcError } = await supabase?.rpc('admin_set_vip_badge', {
        p_label: badgeLabel,
        p_icon: badgeIcon,
      });
      if (rpcError) throw rpcError;
      setBadgeSuccess(true);
      setTimeout(() => setBadgeSuccess(false), 2500);
    } catch (err) {
      setBadgeError(err?.message || 'Errore salvataggio badge');
    }
    setBadgeLoading(false);
  };

  const handleAddManuale = async () => {
    setAddLoading(true);
    setAddError(null);
    setAddSuccess(false);
    try {
      const { error: rpcError } = await supabase?.rpc('admin_exempt_by_email', {
        p_email: addEmail?.trim(),
      });
      if (rpcError) throw rpcError;
      setAddSuccess(true);
      setAddEmail('');
      await fetchUsers();
      await fetchStats();
      setTimeout(() => setAddSuccess(false), 2500);
    } catch (err) {
      setAddError(err?.message || 'Errore aggiunta manuale');
    }
    setAddLoading(false);
  };

  return (
    <AdminDashboardLayout>
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Programma VIP</h1>
        <p className="mt-2 text-gray-500">Gestione account esenti dal pagamento</p>

        {/* Control Panel — 4 cards */}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">

          {/* 1. Stats card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 flex flex-col gap-2">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Statistiche</h2>
            {stats ? (
              <>
                <p className="text-2xl font-bold text-[#013776]">
                  {stats?.founder_count} / {stats?.threshold}
                  <span className="text-sm font-normal text-gray-500 ml-1">fondatori</span>
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium text-gray-800">{stats?.manual_count}</span> manuali
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium text-gray-800">{stats?.total_exempt}</span> totali esenti
                </p>
              </>
            ) : (
              <p className="text-sm text-gray-400">Caricamento...</p>
            )}
          </div>

          {/* 2. Soglia card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 flex flex-col gap-3">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Soglia fondatori</h2>
            <div className="flex gap-2 items-center">
              <input
                type="number"
                min="0"
                value={soglia}
                onChange={(e) => { setSoglia(e?.target?.value); setSogliaError(null); setSogliaSuccess(false); }}
                className="w-24 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#32b3e5] focus:border-transparent"
                placeholder="0"
              />
              <button
                onClick={handleSogliaUpdate}
                disabled={sogliaLoading}
                className="flex-1 px-3 py-2 rounded-lg text-sm font-medium bg-[#013776] text-white hover:bg-[#012a5e] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {sogliaLoading ? 'Aggiornamento...' : 'Aggiorna'}
              </button>
            </div>
            {sogliaError && (
              <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{sogliaError}</p>
            )}
            {sogliaSuccess && (
              <p className="text-xs text-green-600 bg-green-50 border border-green-200 rounded-lg px-3 py-2">Soglia aggiornata.</p>
            )}
          </div>

          {/* 3. Badge card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 flex flex-col gap-3">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Badge VIP</h2>
            <input
              type="text"
              value={badgeLabel}
              onChange={(e) => { setBadgeLabel(e?.target?.value); setBadgeError(null); setBadgeSuccess(false); }}
              placeholder="Etichetta (es. Volaptic VIP)"
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#32b3e5] focus:border-transparent"
            />
            <input
              type="text"
              value={badgeIcon}
              onChange={(e) => { setBadgeIcon(e?.target?.value); setBadgeError(null); setBadgeSuccess(false); }}
              placeholder="Icona emoji (es. 🌟)"
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#32b3e5] focus:border-transparent"
            />
            <button
              onClick={handleBadgeSave}
              disabled={badgeLoading}
              className="px-3 py-2 rounded-lg text-sm font-medium bg-[#013776] text-white hover:bg-[#012a5e] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {badgeLoading ? 'Salvataggio...' : 'Salva'}
            </button>
            {badgeError && (
              <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{badgeError}</p>
            )}
            {badgeSuccess && (
              <p className="text-xs text-green-600 bg-green-50 border border-green-200 rounded-lg px-3 py-2">Badge salvato.</p>
            )}
            <p className="text-xs text-gray-400">Si applica a tutti gli account esenti</p>
          </div>

          {/* 4. Aggiungi manuale card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 flex flex-col gap-3">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Aggiungi manuale</h2>
            <input
              type="email"
              value={addEmail}
              onChange={(e) => { setAddEmail(e?.target?.value); setAddError(null); setAddSuccess(false); }}
              placeholder="email@esempio.it"
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#32b3e5] focus:border-transparent"
            />
            <button
              onClick={handleAddManuale}
              disabled={addLoading || !addEmail?.trim()}
              className="px-3 py-2 rounded-lg text-sm font-medium bg-[#32b3e5] text-white hover:bg-[#2a9fd0] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {addLoading ? 'Aggiunta...' : 'Aggiungi'}
            </button>
            {addError && (
              <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{addError}</p>
            )}
            {addSuccess && (
              <p className="text-xs text-green-600 bg-green-50 border border-green-200 rounded-lg px-3 py-2">Professionista aggiunto.</p>
            )}
          </div>

        </div>

        {/* Existing table */}
        <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Caricamento...</div>
          ) : error ? (
            <div className="p-8 text-center text-red-500">Errore: {error}</div>
          ) : users?.length === 0 ? (
            <div className="p-8 text-center text-gray-400">Nessun professionista esente trovato.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Nome</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Città</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Motivo</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Iscritto il</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider"></th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {users?.map((user) => (
                    <tr key={user?.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-sm text-gray-800 whitespace-nowrap">{user?.email || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-800 whitespace-nowrap">{user?.full_name || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{user?.default_city || '—'}</td>
                      <td className="px-4 py-3 text-sm whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {reasonLabel(user?.reason)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{formatDate(user?.created_at)}</td>
                      <td className="px-4 py-3 text-sm whitespace-nowrap">
                        <button
                          onClick={() => handleRemove(user?.id)}
                          disabled={removingId === user?.id}
                          className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-medium bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {removingId === user?.id ? 'Rimozione...' : 'Rimuovi'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AdminDashboardLayout>
  );
};

export default AdminProgrammaVip;
